<script setup>
import AuthorizedLayout from '@/layouts/AuthorizedLayout.vue';
import moment from 'moment';
</script>

<template>
    <AuthorizedLayout>
        <template v-slot:title>
            <h1 class="m-0 text-white fw-semibold"><span class="fw-normal text-body"> {{$t('dashboard.welcome_message')}},</span> {{user.given_name}}!</h1>
        </template>
        <div class="container mt-5 pt-3">
            <div class="accordion position-relative" id="accordionDashboard">
                <div class="accordion-item bg-transparent border-0">
                    <h2 class="accordion-header mb-4 mb-md-0">
                        <a href="#" class="text-white text-uppercase btn-toggle fw-semibold bg-transparent shadow-none p-0 m-0" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOrders" aria-expanded="true" aria-controls="collapseOrders">
                            <span class="me-1">{{ $t('dashboard.your_orders') }}</span> <font-awesome-icon class="text-primary" :icon="['fas', 'chevron-up']" />
                        </a>
                    </h2>
                    <div id="collapseOrders" class="accordion-collapse collapse show" data-bs-parent="#accordionDashboard">
                        <div class="accordion-body p-0">
                            <div class="leaf d-none d-md-block"></div>
                            <div v-for="order in orders" :key="order.external_order_id" class="pb-4">
                                <div class="order-item bg-light rounded overflow-hidden">
                                    <div class="row p-4 pt-0 pt-md-4 g-4 g-md-0">
                                        <div class="col-md-6 col-xl-2">
                                            <p class="text-secondary mb-2">{{ $t('dashboard.order_nr') }}</p>
                                            <h2 class="mb-0 text-white">#{{ order.external_order_id }}</h2>
                                        </div>
                                        <div class="col-md-6 col-xl-3">
                                            <p class="text-secondary mb-2">{{ $t('dashboard.estimated_delivery') }}</p>
                                            <h2 class="mb-0 text-white">{{ moment(order.order_expected_delivery).unix() <= 0 ? $t('dashboard.delivery_date_unknown') : moment(order.order_expected_delivery).format('DD.MM.yyyy') }}</h2>
                                        </div>
                                        <div class="col-lg-12 col-xl-4 py-4 py-xl-0">
                                            <div class="d-flex h-100 align-items-center">
                                                <div class="progress my-auto rounded-pill w-100">
                                                    <div class="progress-bar" role="progressbar" :style="`width: ${ getOrderProgress(order.order_status) }%`" aria-valuemin="0" aria-valuemax="100"></div>
                                                </div>
                                                <span class="display-6 fw-bold text-white ps-3">{{ getOrderProgress(order.order_status) }}%</span>
                                            </div>
                                        </div>
                                        <div class="col-xl-3 align-self-center ps-xl-5">
                                            <button class="btn btn-primary btn-toggle text-white fw-normal w-100 collapsed" type="button" data-bs-toggle="collapse" :data-bs-target="`#collapseDetails-${order.external_order_id}`" aria-expanded="false" :aria-controls="`collapseDetails-${order.external_order_id}`">
                                                Details <font-awesome-icon :icon="['fas', 'chevron-up']" />
                                            </button>
                                        </div>
                                    </div>
                                    <div class="collapse" :id="`collapseDetails-${order.external_order_id}`">
                                        <div class="card card-body bg-light rounded-0 px-4">
                                            <div class="row g-4">
                                                <div class="col-lg-4">
                                                    <div class="text-center image-shadow position-relative">
                                                        <img class="img-fluid w-75 position-relative" :src="require('../assets/miner/' + order.product_sku.toLowerCase() + '.png')" />
                                                    </div>

                                                    <h3 class="mt-4"><span class="text-secondary"></span>{{ order.product_name }}</h3>
                                                    <table class="table-dark table-xs text-body w-100">
                                                        <tbody>
                                                            <tr>
                                                                <td class="pb-2 text-white">{{ $t('dashboard.order_date') }}:</td>
                                                                <td class="text-end">{{ moment(order.order_date).format('DD.MM.yyyy') }}</td>
                                                            </tr>
                                                            <tr class="border-top border-secondary">
                                                                <td class="py-2 text-white">{{ $t('dashboard.hosting') }}</td>
                                                                <td class="text-end">{{ order.order_hosting ? $t('yes') : $t('no')}}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                    <table class="table-dark table-xs text-body w-100 mt-4">
                                                        <tbody>
                                                            <tr>
                                                                <td class="pb-2 text-white">{{ $t('dashboard.order_amount') }}:</td>
                                                                <td class="text-end">{{ order.order_amount }}</td>
                                                            </tr>
                                                            <tr class="border-top border-secondary">
                                                                <td class="py-2 text-white">{{ $t('dashboard.price_per_unit') }}:</td>
                                                                <td class="text-end">{{ formatPrice(order.order_sum / order.order_amount)}}</td>
                                                            </tr>
                                                            <tr class="border-top border-secondary">
                                                                <td class="py-2 text-white">{{ $t('dashboard.subtotal') }}:</td>
                                                                <td class="text-end">{{ formatPrice(order.order_sum) }}</td>
                                                            </tr>
                                                            <tr class="border-top border-secondary">
                                                                <td class="py-2 text-white">{{ $t('dashboard.tax') }} ({{ order.order_vat }}%):</td>
                                                                <td class="text-end">{{ formatPrice(order.order_total - order.order_sum) }}</td>
                                                            </tr>
                                                            <tr class="border-secondary border-top">
                                                                <td class="py-2 text-white">{{ $t('dashboard.total') }}</td>
                                                                <td class="text-end text-white">{{ formatPrice(order.order_total) }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>

                                                    <!-- NOTE: Invoice download not implemented in API yet -->
                                                    <!-- <a class="btn btn-outline-secondary text-body px-3 py-2 btn-sm rounded-pill mt-2 fs-6" href="#">Download Rechnung</a> -->
                                                </div>
                                                <div class="offset-lg-1 col-lg-7">
                                                    <h3>{{ $t('dashboard.estimated_delivery') }}: {{ moment(order.order_expected_delivery).unix() <= 0 ? $t('dashboard.delivery_date_unknown') : moment(order.order_expected_delivery).format('DD.MM.yyyy') }}</h3>
                                                    <p>{{ $t('dashboard.estimated_delivery_info') }}</p>
                                                    <div class="state-wrapper position-relative">
                                                        <div class="state-progressbar progress my-auto rounded-pill w-100">
                                                            <div class="progress-bar" role="progressbar" :style="`width: ${ getOrderProgress(order.order_status)}%; height: ${ getOrderProgress(order.order_status)}%`" aria-valuemin="0" aria-valuemax="100"></div>
                                                        </div>
                                                        <div class="states d-flex flex-column flex-lg-row justify-content-evenly state-titles">
                                                            <div :class="`small p-3 text-center position-relative ${getOrderProgress(order.order_status) >= 20 ? 'text-white  fw-semibold' : text-body }`">
                                                                <div class="state">{{ $t('dashboard.delivery_state.cash_receipt') }}</div>
                                                            </div>
                                                            <div :class="`small p-3 text-center position-relative ${getOrderProgress(order.order_status) >= 40 ? 'text-white  fw-semibold' : text-body }`">
                                                                <div class="state">{{ $t('dashboard.delivery_state.shipped_to_us') }}</div>
                                                            </div>
                                                            <div :class="`small p-3 text-center position-relative  ${getOrderProgress(order.order_status) >= 60 ? 'text-white  fw-semibold' : text-body }`">
                                                                <div class="state">{{ $t('dashboard.delivery_state.testing') }}</div>
                                                            </div>
                                                            <div :class="`small p-3 text-center position-relative  ${getOrderProgress(order.order_status) >= 80 ? 'text-white  fw-semibold' : text-body }`">
                                                                <div class="state">{{ $t('dashboard.delivery_state.dispatched') }}</div>
                                                                <a v-if="order.order_tracking_url" class="btn btn-outline-secondary text-body p-2 btn-sm rounded-pill mt-2" :href="order.order_tracking_url" target="_blank">Tracking</a>
                                                            </div>
                                                            <div :class="`small p-3 text-center position-relative ${getOrderProgress(order.order_status) == 100 ? 'text-white  fw-semibold' : text-body }`">
                                                                <div class="state">{{ $t('dashboard.delivery_state.delivered') }}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="accordion-item bg-transparent border-0">
                    <h2 class="accordion-header mb-3">
                        <a href="#" class="collapsed text-white text-uppercase btn-toggle fw-semibold bg-transparent shadow-none p-0 pt-4" type="button" data-bs-toggle="collapse" data-bs-target="#collapseHosting" aria-expanded="true" aria-controls="collapseHosting">
                            <span class="me-1">{{ $t('dashboard.your_hosting') }}</span> <font-awesome-icon class="text-primary" :icon="['fas', 'chevron-up']" />
                        </a>
                    </h2>
                    <div id="collapseHosting" class="accordion-collapse collapse" data-bs-parent="#accordionDashboard">
                        <div class="accordion-body p-0">
                            {{ $t('dashboard.your_hosting_text') }} 👋
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AuthorizedLayout>
</template>

<script>
import { useUserStore } from '@/js/user.js';

export default { 
    data() {
        return {
            user: null,
            orders: null
        }
    },
    beforeMount() {
        const user = useUserStore()
        this.user = user.$state.user

        user.getOrders().then((response) => {
            if(response.status == 200)
                this.orders = response.data
        });
    },
    methods: {
        formatPrice(value) {
            var formatter = new Intl.NumberFormat('de-DE', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2
            });
            return formatter.format(value);
        },
        getOrderProgress(id) {
            switch(id) {
                case "78f22a30-e14a-11ee-8d86-0800200c9a66":
                    return 0;    

                case "73ca50c8-90ce-4431-b05b-894b1176e7d7":
                    return 20;

                case "e876a75c-4988-4367-ac8d-e5a8e39aec2a":
                    return 40;

                case "ee384ae9-05d3-4632-a935-186cd9d6bc93":
                    return 60;

                case "dde6e68b-7b92-432b-b1bf-5e78be8864e8":
                    return 80;

                case "4c94b9d3-f103-4547-b524-82701a649f97":
                    return 100
            }

            return 0;
        }
    }
}

</script>

<style lang="scss">
    body {
        overflow-x: hidden;
    }

    #collapseDetails .card-body {
        border-top: 5px dotted $secondary
    }

    .leaf {
        width: 100px;
        height: 100px;
        background-image: url('/src/assets/dashboard/leafs.png');
        background-size: contain;
        margin-left: calc(100% - 50px);
        margin-bottom: -70px;
    }

    #collapseOrders {
        overflow-x: visible !important;
        overflow-y: clip !important;
        
        .order-item {
            position: relative;

            .image-shadow:before {
                content: '';
                position: absolute;
                top: 30%;
                right: 30%;
                bottom: 30%;
                left: 30%;
                box-shadow: 0 0 65px 15px white;
                z-index: 0;
            }

        }
    }

    @media(min-width: 992px) {
        .state-wrapper {
            .state-progressbar {
                .progress-bar {
                    height: 100% !important;
                }   
            }

            .state-titles > * {
                flex: 1;

                &:before {
                    content: '';
                    position: absolute;
                    height: 6px;
                    width: 2px;
                    background-color: $body-color;
                    top: 6px;
                    right: 0;
                    left: 0;
                    margin: auto;

                    &.text-white {
                        background-color: #fff;
                    }
                }
            }
        }
    }

    @media(max-width: 991px) {
        .state-wrapper {
        
            .state-progressbar {
                height: 500px;
                width: 1rem !important;

                .progress-bar {
                    width: 100% !important;
                }
            }

            .states {
                position: absolute;
                top: 0;
                bottom: 0;
                left: 2rem;
                right: 0;
            }

            .state-titles > * {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: start;
                justify-content: center;

                &:before {
                    content: '';
                    position: absolute;
                    height: 2px;
                    width: 6px;
                    background-color: $body-color;
                    top: 0;
                    bottom: 0;
                    left: 0;
                    margin: auto;

                    &.text-white {
                        background-color: #fff;
                    }
                }
            }
        }
    }
</style>